import React, { useCallback, useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useForm from "../../../hooks/useForm";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import { unitFields } from "../../../constants/forms/unitFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";

const CreateUnitPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      abbreviation: [],
      deleted_at: false,
    },
    "equipments_units"
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange } = useForm(setDefaultFieldValues(unitFields));

  // --- Mutação para criar uma nova Unidade ---
  const createUnitMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.units.create,
        dataToCreate
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        navigate(`/aluguel/unidades`, { state: { type: "success" } });
        queryClient.invalidateQueries({
          queryKey: ["equipments_units", filters, pagination],
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        unit: {
          ...cleanedData.unit,
        },
      };
      await createUnitMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar unidade:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createUnitMutation]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/unidades");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return unitFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Unidades"
        showBackButton={true}
        backUrl="/aluguel/unidades"
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar Unidade"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createUnitMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateUnitPage;
